// src/settings.js
import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from "react";

const SettingsContext = createContext(null);

const DEFAULT_SETTINGS = Object.freeze({
  planTopK: 5,
  selectedWalletId: null, // number | null
});

const LS_KEY = "finbot_settings_v1";

function sanitizeSelectedWalletId(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return null;
  const id = Math.trunc(n);
  return id > 0 ? id : null;
}

function sanitizePlanTopK(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return DEFAULT_SETTINGS.planTopK;
  return Math.min(50, Math.max(1, Math.round(n)));
}

function loadFromLocalStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;

    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;

    return {
      planTopK: sanitizePlanTopK(obj.planTopK),
      selectedWalletId: sanitizeSelectedWalletId(obj.selectedWalletId),
    };
  } catch {
    return null;
  }
}

function saveToLocalStorage(settings) {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        planTopK: settings.planTopK,
        selectedWalletId: settings.selectedWalletId,
      })
    );
  } catch {}
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    const saved = loadFromLocalStorage();
    return { ...DEFAULT_SETTINGS, ...(saved || {}) };
  });

  // persistência
  useEffect(() => {
    saveToLocalStorage(settings);
  }, [settings]);

  // ✅ para casos em que outro código mexe no localStorage e você quer “re-hidratar”
  const reloadFromLocal = useCallback(() => {
    const saved = loadFromLocalStorage();
    setSettings((s) => ({ ...(s || DEFAULT_SETTINGS), ...(saved || {}) }));
  }, []);

  const update = useCallback((patch) => {
    setSettings((s) => {
      const base = s || DEFAULT_SETTINGS;
      const next = { ...base, ...(patch || {}) };

      next.planTopK = sanitizePlanTopK(next.planTopK);
      next.selectedWalletId = sanitizeSelectedWalletId(next.selectedWalletId);

      return next;
    });
  }, []);


  const clearLocal = useCallback(() => {
    try {
      localStorage.removeItem(LS_KEY);
    } catch {}
    setSettings({ ...DEFAULT_SETTINGS });
  }, []);

  // dentro do SettingsProvider, junto do update/clearLocal
  const setSelectedWalletIdIfEmpty = useCallback((walletId) => {
    setSettings((s) => {
      const current = sanitizeSelectedWalletId(s?.selectedWalletId);
      if (current) return s; // já tem
      const nextId = sanitizeSelectedWalletId(walletId);
      if (!nextId) return s;
      return { ...(s || DEFAULT_SETTINGS), selectedWalletId: nextId };
    });
  }, []);

  const value = useMemo(
    () => ({
      settings,
      loading: false,
      update,
      reloadFromLocal,
      setSelectedWalletIdIfEmpty,
      clearLocal,
    }),
    [settings, update, reloadFromLocal, setSelectedWalletIdIfEmpty, clearLocal]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used inside <SettingsProvider />");
  return ctx;
}
